import { reportError } from "@/utils/reportError";
import { trackDailyActionsCompleted } from '@/utils/analytics';
import { CHALLENGE_END, CHALLENGE_START, toRealDate } from '@/constants/challenge';
import { ACTIONS } from '@/data/actions';
import { getTodayCH } from '@/utils/date';
import { supabase } from '@/utils/supabase';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface DailyAction {
  id: string;
  actionId: number;
  actionDate: string;
  count: number;
  pointsEarned: number;
}

/**
 * Retour de saveActions — reflet du crédit SERVEUR (RPC validate_daily_actions).
 * `creditedActionIds` = actions réellement insérées (déduplication serveur).
 * `actionPoints`/`bonusPoints`/`totalPointsAdded` = ce que le serveur a crédité.
 * `actionsToday` = total distinct du jour ; `allDone` = 9/9 actions faites.
 */
export interface SaveActionsResult {
  success: boolean;
  creditedActionIds: number[];
  actionPoints: number;
  bonusPoints: number;
  totalPointsAdded: number;
  actionsToday: number;
  allDone: boolean;
}

const EMPTY_SAVE_RESULT: SaveActionsResult = {
  success: false,
  creditedActionIds: [],
  actionPoints: 0,
  bonusPoints: 0,
  totalPointsAdded: 0,
  actionsToday: 0,
  allDone: false,
};

export function useDailyActions(viewingDate?: string) {
  const [todayActions, setTodayActions] = useState<DailyAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const viewingDateString = viewingDate ?? null;

  // Garde anti-stale : si la date change pendant qu'une requête est en vol,
  // la réponse de l'ancienne date ne doit pas écraser celle de la nouvelle.
  const latestRequestRef = useRef(0);

  const fetchTodayActions = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    const isStale = () => requestId !== latestRequestRef.current;
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        if (!isStale()) setLoading(false);
        return;
      }

      const dateToQuery = viewingDateString ?? getTodayCH();

      const { data, error: fetchError } = await supabase
        .from('daily_actions')
        .select('*')
        .eq('user_id', user.id)
        .eq('action_date', dateToQuery);

      if (isStale()) return;
      if (fetchError) throw fetchError;

      setTodayActions(
        (data ?? []).map(d => ({
          id: d.id,
          actionId: d.action_id,
          actionDate: d.action_date,
          count: d.count,
          pointsEarned: d.points_earned,
        }))
      );
    } catch (err: any) {
      reportError(err);
      if (!isStale()) setError(err.message);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [viewingDateString]);

  // Au changement de jour affiché, on vide immédiatement la liste : sinon les
  // actions du jour précédent restent visibles (cochées « déjà fait ») le temps
  // que la nouvelle requête réponde → faux « validé » en naviguant entre jours.
  useEffect(() => {
    setTodayActions([]);
  }, [viewingDateString]);

  useEffect(() => {
    fetchTodayActions();
  }, [fetchTodayActions]);

  const saveActions = async (
    actionIds: number[],
    // Legacy 2nd arg kept for backwards-compat — no longer used. Chaque valeur
    // de points (et le bonus ligne +50) est désormais déterminée par le serveur.
    _pointsPerAction: number = 10
  ): Promise<SaveActionsResult> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return EMPTY_SAVE_RESULT;

      const today = getTodayCH();

      // Les validations ne concernent QUE le jour suisse courant. Si l'écran
      // affiche un autre jour (consultation passée/future du challenge), on
      // refuse : le serveur force de toute façon action_date = aujourd'hui
      // (trigger enforce_daily_action_integrity), donc valider en consultant
      // un autre jour écrirait sur « aujourd'hui » à l'insu de l'utilisateur.
      if (viewingDateString && viewingDateString !== today) {
        return EMPTY_SAVE_RESULT;
      }

      // Crédit 100 % SERVEUR et idempotent : la RPC déduplique (n'insère que
      // les actions pas déjà faites aujourd'hui), lit les points depuis la
      // table `actions`, calcule le bonus ligne +50 et crédite user_scores.
      // Plus AUCUN crédit côté client — c'est ce qui fermait le trou de
      // sur-crédit (état local périmé → re-crédit des points/bonus).
      const { data, error: rpcError } = await supabase.rpc('validate_daily_actions', {
        p_action_ids: actionIds,
      });

      if (rpcError) throw rpcError;

      const result: SaveActionsResult = {
        success: true,
        creditedActionIds: (data?.credited_action_ids ?? []) as number[],
        actionPoints: (data?.action_points ?? 0) as number,
        bonusPoints: (data?.bonus_points ?? 0) as number,
        totalPointsAdded: (data?.total_points_added ?? 0) as number,
        actionsToday: (data?.actions_today ?? 0) as number,
        allDone: (data?.all_done ?? false) as boolean,
      };

      if (result.creditedActionIds.length > 0) {
        const creditedWithPoints = result.creditedActionIds.map((actionId) => {
          const action = ACTIONS.find((a) => a.id === actionId);
          return { actionId, points: action?.points ?? 0 };
        });
        trackDailyActionsCompleted(result.creditedActionIds.length, result.actionPoints);
        // Optimistic local update : n'ajoute QUE les actions réellement
        // créditées par le serveur (celles pas encore présentes) pour éviter
        // les doublons dans l'état local.
        setTodayActions((prev) => {
          const known = new Set(prev.map((a) => a.actionId));
          const toAppend = creditedWithPoints
            .filter(({ actionId }) => !known.has(actionId))
            .map(({ actionId, points }) => ({
              id: `optimistic-${actionId}-${today}`,
              actionId,
              actionDate: today,
              count: 1,
              pointsEarned: points,
            }));
          return toAppend.length > 0 ? [...prev, ...toAppend] : prev;
        });
      }

      return result;
    } catch (err: any) {
      reportError(err);
      setError(err.message);
      return EMPTY_SAVE_RESULT;
    }
  };

  const getCompletedActionIds = (): number[] => {
    return todayActions.map(a => a.actionId);
  };

  const hasCompletedToday = todayActions.length > 0;

  return {
    todayActions,
    loading,
    error,
    saveActions,
    getCompletedActionIds,
    hasCompletedToday,
    refetch: fetchTodayActions,
  };
}

// ---------------------------------------------------------------------------
// Historique du challenge (vue « Semaine » — récap lecture seule)
// ---------------------------------------------------------------------------

export interface ChallengeHistoryEntry {
  actionId: number;
  /** Jour RÉEL en base ('YYYY-MM-DD'). Sert uniquement à regrouper par jour
   * (lignes complètes) — pas besoin de le reconvertir en jour affiché. */
  actionDate: string;
  pointsEarned: number;
}

/**
 * Toutes les actions validées par l'utilisateur sur la fenêtre du challenge.
 * CHALLENGE_START/END sont des jours AFFICHÉS (calendrier du concours) : en
 * mode test (SIMULATE_CONTEST_WEEK) les écritures en base portent le jour
 * RÉEL, donc les bornes de la requête sont converties via toRealDate().
 * En prod, toRealDate() est l'identité.
 */
export function useChallengeHistory() {
  const [history, setHistory] = useState<ChallengeHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setHistory([]);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('daily_actions')
        .select('action_id, action_date, points_earned')
        .eq('user_id', user.id)
        .gte('action_date', toRealDate(CHALLENGE_START))
        .lte('action_date', toRealDate(CHALLENGE_END));

      if (fetchError) throw fetchError;

      setHistory(
        (data ?? []).map((d) => ({
          actionId: d.action_id,
          actionDate: d.action_date,
          pointsEarned: d.points_earned,
        }))
      );
      setError(null);
    } catch (err: any) {
      reportError(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { history, loading, error, refetch: fetchHistory };
}
